import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  AlertCircle,
  ArrowUp,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Loader2,
  Lock,
  Sparkles,
} from "lucide-react";
import {
  analyzeWorkspaceIntegrationInput,
  createWorkspaceIntegration,
  createWorkspaceIntegrationImport,
  revokeWorkspaceIntegration,
  testWorkspaceIntegration,
  updateWorkspaceIntegrationPolicy,
} from "../../lib/api";
import type {
  WorkspaceIntegrationCatalogTool,
  WorkspaceIntegrationCopilotDetection,
  WorkspaceIntegrationCreateRequest,
  WorkspaceIntegrationImportRequest,
} from "../../lib/api";

// The Integration Copilot is a chat front door to the existing
// workspace-integration pipeline. The user pastes one thing (MCP URL, OpenAPI
// spec, GraphQL endpoint/SDL, llms.txt URL) and the copilot walks a staged
// conversation: detection card -> tools checklist -> save summary -> test
// call, each rendered as a widget inside the transcript. Persisting reuses
// the existing import/create/policy/test API functions.

type Stage = "idle" | "detect" | "tools" | "review" | "saved" | "done";

type TranscriptItem =
  | { id: number; type: "msg"; role: "user" | "assistant" | "clarify" | "error"; text: string }
  | { id: number; type: "widget"; widget: "detection" | "tools" | "review" | "test" | "connect" };

// A pre-built catalog app surfaced as a one-tap chip in the chat. "oauth"
// connects in one click (safe default permissions, approval on the provider's
// screen); "mcp" feeds the app's known MCP endpoint through the normal
// analyze flow with zero typing; "detail" hands off to the app's page.
export interface CopilotQuickAdd {
  id: string;
  name: string;
  Icon?: React.ComponentType<{ className?: string }>;
  connected: boolean;
  kind: "oauth" | "mcp" | "detail";
  endpoint?: string;
  developer?: string;
  permissionsSummary?: string;
}

// Strip conversational lead-ins so "connect gmail" or "add Google Drive"
// matches a quick-add chip by name.
const QUICK_ADD_VERB = /^(please\s+)?(connect|add|set\s*up|setup|install|use|open|link)\s+/i;

function matchQuickAdd(input: string, quickAdds: CopilotQuickAdd[]): CopilotQuickAdd | null {
  const trimmed = input.trim();
  if (trimmed.length > 48 || trimmed.includes("\n") || trimmed.includes("://")) return null;
  const normalized = trimmed.replace(QUICK_ADD_VERB, "").trim().toLowerCase();
  if (!normalized) return null;
  const squashed = normalized.replace(/[^a-z0-9]/g, "");
  const wholeSquashed = trimmed.toLowerCase().replace(/[^a-z0-9]/g, "");
  let contained: CopilotQuickAdd | null = null;
  let containedCount = 0;
  for (const app of quickAdds) {
    const name = app.name.toLowerCase();
    const nameSquashed = name.replace(/[^a-z0-9]/g, "");
    if (normalized === name || squashed === nameSquashed || normalized === app.id.toLowerCase()) {
      return app;
    }
    // Forgiving fallback: "conect gmail", "set up gmail please" — the app
    // name appears inside a short message. Only used when exactly one app
    // matches, so ambiguous inputs still get the clarify response.
    if (nameSquashed.length >= 4 && wholeSquashed.includes(nameSquashed)) {
      contained = app;
      containedCount += 1;
    }
  }
  return containedCount === 1 ? contained : null;
}

type TestOutcome =
  | { status: "pass"; summary: string }
  | { status: "skipped"; note: string }
  | { status: "fail"; message: string };

const KIND_LABELS: Record<WorkspaceIntegrationCopilotDetection["kind"], string> = {
  "mcp-remote": "MCP server",
  openapi: "OpenAPI import",
  graphql: "GraphQL import",
  "llms-txt": "llms.txt import",
};

const GREETING =
  "Hi! I set up integrations for this workspace. Paste any one thing — an MCP server URL, an OpenAPI spec, a GraphQL endpoint, or an llms.txt link — and I'll detect the format, pull out the config, and only ask for what's genuinely missing.";

// Single source for the default-permissions promise shown on OAuth quick-add
// chips and the connect card.
export const COPILOT_DEFAULT_PERMISSIONS_SUMMARY =
  "Read access on by default · write actions stay blocked until you allow them.";

const PASTE_PLACEHOLDER = "Paste an MCP URL, OpenAPI spec, GraphQL endpoint, or llms.txt…";

// Delay before revealing the next conversational step, so the flow reads as a
// dialogue instead of a form dump. Local-only pacing; network steps show the
// typing indicator for their real duration.
const PACING_MS = 350;

// Stable id so the secret guard can move focus to the secure token input when
// someone tries to paste a credential into the chat.
const TOKEN_INPUT_ID = "copilot-token-input";

// Client-side mirror of the backend's copilotSecretPatterns
// (backend/internal/api/workspace_integrations_copilot_sniff.go). The backend
// refuses these too with a clarify event (defense in depth), but the client
// must catch them first so the secret never leaves the browser. Keep both
// lists in sync.
const COPILOT_SECRET_PATTERNS: RegExp[] = [
  /^\s*authorization\s*:/im,
  /['"]?authorization['"]?\s*[:=]/i,
  /\bbearer\s+[A-Za-z0-9_\-./+=]{12,}/i,
  /\bsk-[A-Za-z0-9_-]{16,}/,
  /\bsk_(?:live|test)_[A-Za-z0-9]{16,}/,
  /\b(?:ghp|gho|ghu|ghs|ghr)_[A-Za-z0-9]{20,}/,
  /\bgithub_pat_[A-Za-z0-9_]{20,}/,
  /\bshp(?:at|ss|ca|pa)_[A-Za-z0-9]{16,}/,
  /\bxox[abpsr]-[A-Za-z0-9-]{10,}/,
  /\bAKIA[0-9A-Z]{16}\b/,
  /\bAIza[0-9A-Za-z_-]{30,}/,
  // Bare JWT: three dot-separated base64url segments.
  /\beyJ[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{5,}\.[A-Za-z0-9_-]{5,}\b/,
];

function copilotLooksLikeSecret(text: string): boolean {
  return COPILOT_SECRET_PATTERNS.some((pattern) => pattern.test(text));
}

function defaultToolEnabled(
  tool: WorkspaceIntegrationCatalogTool,
  allowed: string[],
  denied: string[],
): boolean {
  if (denied.includes(tool.name)) return false;
  if (allowed.length > 0 && !allowed.includes(tool.name)) return false;
  return true;
}

function sameStringSet(a: string[], b: string[]) {
  if (a.length !== b.length) return false;
  const set = new Set(a);
  return b.every((value) => set.has(value));
}

function safeExternalHttpUrl(raw: string | undefined): string | undefined {
  const value = raw?.trim();
  if (!value) return undefined;
  try {
    const parsed = new URL(value);
    if (parsed.protocol !== "http:" && parsed.protocol !== "https:") return undefined;
    return parsed.href;
  } catch {
    return undefined;
  }
}

export function CopilotPanel({
  accountId,
  workspaceId,
  onSaved,
  quickAdds = [],
  onOAuthConnect,
  onOpenDetail,
  externalDetect = null,
}: {
  accountId: number;
  workspaceId?: string;
  onSaved: () => void;
  quickAdds?: CopilotQuickAdd[];
  onOAuthConnect?: (id: string) => Promise<void>;
  onOpenDetail?: (id: string) => void;
  /** External detect request (e.g. the catalog's "not found — detect it"
   * row). A new nonce expands the panel and runs the analyzer on `input`. */
  externalDetect?: { input: string; nonce: number } | null;
}) {
  // Collapsed/expanded preference sticks across visits.
  const [open, setOpen] = useState(() => {
    try {
      return window.localStorage.getItem("ocm-copilot-open") !== "0";
    } catch {
      return true;
    }
  });
  const toggleOpen = useCallback(() => {
    setOpen((current) => {
      try {
        window.localStorage.setItem("ocm-copilot-open", current ? "0" : "1");
      } catch {
        // Private mode / storage disabled: the toggle still works for the session.
      }
      return !current;
    });
  }, []);
  const [input, setInput] = useState("");
  const [items, setItems] = useState<TranscriptItem[]>([
    { id: 0, type: "msg", role: "assistant", text: GREETING },
  ]);
  const [stage, setStage] = useState<Stage>("idle");
  const [pacing, setPacing] = useState(false);
  const [detection, setDetection] = useState<WorkspaceIntegrationCopilotDetection | null>(null);
  const [lastInput, setLastInput] = useState("");
  const [fieldValues, setFieldValues] = useState<Record<string, string>>({});
  const [enabledTools, setEnabledTools] = useState<Record<string, boolean>>({});
  const [analyzing, setAnalyzing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedSlug, setSavedSlug] = useState<string | null>(null);
  const [testing, setTesting] = useState(false);
  const [testOutcome, setTestOutcome] = useState<TestOutcome | null>(null);
  const [failResolved, setFailResolved] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [connectApp, setConnectApp] = useState<CopilotQuickAdd | null>(null);
  const [connectBusy, setConnectBusy] = useState(false);
  const [liveStatus, setLiveStatus] = useState<string | null>(null);
  const itemSeq = useRef(0);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const paceTimer = useRef<number | null>(null);

  const nextId = () => {
    itemSeq.current += 1;
    return itemSeq.current;
  };

  const pushMsg = useCallback((role: "user" | "assistant" | "clarify" | "error", text: string) => {
    setItems((current) => [...current, { id: nextId(), type: "msg", role, text }]);
  }, []);

  const pushWidget = useCallback((widget: "detection" | "tools" | "review" | "test" | "connect") => {
    setItems((current) => [...current, { id: nextId(), type: "widget", widget }]);
  }, []);

  // Reveal the next conversational beat after a short typing pause.
  const pace = useCallback((fn: () => void) => {
    setPacing(true);
    paceTimer.current = window.setTimeout(() => {
      setPacing(false);
      fn();
    }, PACING_MS);
  }, []);

  useEffect(() => () => {
    if (paceTimer.current !== null) window.clearTimeout(paceTimer.current);
  }, []);

  // Keep the newest message in view as the conversation grows.
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [items, pacing, analyzing, testing]);

  // Catalog "detect it" handoff: a new nonce expands the panel and analyzes.
  const lastDetectNonce = useRef(0);
  useEffect(() => {
    if (!externalDetect || externalDetect.nonce === lastDetectNonce.current) return;
    lastDetectNonce.current = externalDetect.nonce;
    setOpen(true);
    void runAnalyze(externalDetect.input);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [externalDetect]);

  // After a step transition the previously clicked button is disabled and
  // keyboard focus falls to <body>; move it onto the newly revealed step's
  // primary action. Never steal focus from an input the user is typing in.
  useEffect(() => {
    if (stage !== "tools" && stage !== "review") return;
    if (document.activeElement && document.activeElement !== document.body) return;
    document.getElementById("copilot-active-primary")?.focus();
  }, [stage, items.length]);

  const resetFlowState = useCallback(() => {
    // Widget cards belong to the flow that produced them; a new flow (or a
    // removal) supersedes them. The text conversation stays.
    setItems((current) => current.filter((item) => item.type !== "widget"));
    setStage("idle");
    setConnectApp(null);
    setConnectBusy(false);
    setDetection(null);
    setFieldValues({});
    setEnabledTools({});
    setSavedSlug(null);
    setTestOutcome(null);
    setFailResolved(false);
  }, []);

  const runAnalyze = useCallback(
    async (rawInput: string, token?: string) => {
      const trimmed = rawInput.trim();
      if (!trimmed || analyzing) return;
      pushMsg("user", token ? "Provided an access token — analyzing again." : trimmed.slice(0, 400));
      setAnalyzing(true);
      resetFlowState();
      setLastInput(trimmed);
      try {
        const response = await analyzeWorkspaceIntegrationInput(
          accountId,
          workspaceId,
          {
            input: trimmed,
            ...(token ? { token } : {}),
          },
          setLiveStatus,
        );
        for (const event of response.events ?? []) {
          if (event.type === "detection" && event.detection) {
            const next = event.detection;
            setDetection(next);
            const values: Record<string, string> = {};
            for (const field of next.fields ?? []) {
              // Never echo the token back into an input; the user re-types it.
              values[field.key] = field.secret ? "" : field.value ?? "";
            }
            setFieldValues(values);
            const allowed = next.allowed_tools ?? [];
            const denied = next.denied_tools ?? [];
            const toggles: Record<string, boolean> = {};
            for (const tool of next.tools ?? []) {
              toggles[tool.name] = defaultToolEnabled(tool, allowed, denied);
            }
            setEnabledTools(toggles);
            setStage("detect");
            pushWidget("detection");
          } else if (event.text) {
            pushMsg(event.type === "clarify" ? "clarify" : "assistant", event.text);
          }
        }
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : "Analysis failed.";
        pushMsg("error", message);
      } finally {
        setAnalyzing(false);
        setLiveStatus(null);
      }
    },
    [accountId, analyzing, pushMsg, pushWidget, resetFlowState, workspaceId],
  );

  const handleQuickAdd = useCallback(
    (app: CopilotQuickAdd) => {
      if (analyzing || connectBusy) return;
      if (app.connected) {
        pushMsg("user", `Open ${app.name}`);
        pushMsg("assistant", `${app.name} is already connected — taking you to its page.`);
        onOpenDetail?.(app.id);
        return;
      }
      if (app.kind === "mcp" && app.endpoint) {
        // Curated MCP app: its endpoint is known, so the normal analyze flow
        // (probe -> tools -> save -> test) runs with zero typing.
        void runAnalyze(app.endpoint);
        return;
      }
      if (app.kind === "oauth" && onOAuthConnect) {
        pushMsg("user", `Connect ${app.name}`);
        resetFlowState();
        setConnectApp(app);
        pace(() => {
          pushMsg(
            "assistant",
            `${app.name} is pre-built — one click and you approve it on the provider's screen. ${app.permissionsSummary ?? COPILOT_DEFAULT_PERMISSIONS_SUMMARY}`,
          );
          pushWidget("connect");
        });
        return;
      }
      pushMsg("user", `Connect ${app.name}`);
      pushMsg("assistant", `${app.name} has its own short setup — taking you there.`);
      onOpenDetail?.(app.id);
    },
    [analyzing, connectBusy, onOAuthConnect, onOpenDetail, pace, pushMsg, pushWidget, resetFlowState, runAnalyze],
  );

  const handleConnectContinue = useCallback(async () => {
    if (!connectApp || !onOAuthConnect || connectBusy) return;
    setConnectBusy(true);
    try {
      // On success the browser redirects to the provider's consent screen;
      // the button stays busy until navigation happens.
      await onOAuthConnect(connectApp.id);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : `Failed to start ${connectApp.name} connect.`;
      pushMsg("error", message);
      setConnectBusy(false);
    }
  }, [connectApp, connectBusy, onOAuthConnect, pushMsg]);

  const handleConnectCancel = useCallback(() => {
    if (connectBusy) return;
    resetFlowState();
    pushMsg("assistant", "No problem — nothing was connected. Tap another app or paste an API below.");
  }, [connectBusy, pushMsg, resetFlowState]);

  const handleSend = useCallback(() => {
    const trimmed = input.trim();
    if (!trimmed) return;
    // Secret guard: never send credentials to the API and never render them in
    // the transcript. The secure Access token field is the only path for them.
    if (copilotLooksLikeSecret(trimmed)) {
      setInput("");
      const hasTokenField = (detection?.fields ?? []).some((field) => field.key === "token");
      pushMsg(
        "assistant",
        hasTokenField
          ? "That looked like it contained a credential, so I discarded it without sending it anywhere. Paste the token into the secure Access token field in the Authorization section of the card instead — never into the chat."
          : "That looked like it contained a credential, so I discarded it without sending it anywhere. Tokens go into the secure Access token field on the connection card — never into the chat.",
      );
      if (hasTokenField) {
        document.getElementById(TOKEN_INPUT_ID)?.focus();
      }
      return;
    }
    // Typing an app's name ("gmail", "connect notion") behaves exactly like
    // tapping its quick-add chip.
    const quickAdd = matchQuickAdd(trimmed, quickAdds);
    if (quickAdd) {
      setInput("");
      handleQuickAdd(quickAdd);
      return;
    }
    setInput("");
    void runAnalyze(trimmed);
  }, [detection, handleQuickAdd, input, pushMsg, quickAdds, runAnalyze]);

  const fields = useMemo(() => detection?.fields ?? [], [detection]);
  const tools = useMemo(() => detection?.tools ?? [], [detection]);
  const missingRequiredKeys = useMemo(
    () => fields.filter((field) => field.required && !(fieldValues[field.key] ?? "").trim()).map((field) => field.key),
    [fields, fieldValues],
  );
  const saveRequest = detection?.create_request ?? detection?.import_request ?? null;
  // A detection without a save body (e.g. a 401 that needs a token before the
  // server will list tools) can only be re-analyzed, not saved.
  const needsReanalyze = Boolean(
    detection && !saveRequest && fields.some((field) => field.key === "token"),
  );
  const canContinue = Boolean(detection && saveRequest && missingRequiredKeys.length === 0);
  const displayName =
    (fieldValues.display_name ?? "").trim() || detection?.display_name || detection?.slug || "this integration";

  const policyFromToggles = useCallback(() => {
    const defaultAllowed = detection?.allowed_tools ?? [];
    const defaultDenied = detection?.denied_tools ?? [];
    const enabled = tools.filter((tool) => enabledTools[tool.name]).map((tool) => tool.name);
    // Deny wins: every unchecked tool is denied explicitly. The allow-list is
    // only kept in allow-list mode (backend sent a non-empty allowed list).
    const denied = tools.filter((tool) => !enabledTools[tool.name]).map((tool) => tool.name);
    const allowed = defaultAllowed.length > 0 ? enabled : [];
    const changed = !sameStringSet(denied, defaultDenied) || !sameStringSet(allowed, defaultAllowed);
    return { allowed, denied, changed };
  }, [detection, enabledTools, tools]);

  const handleDetectionContinue = useCallback(() => {
    if (stage !== "detect" || !canContinue) return;
    if (tools.length > 0) {
      pace(() => {
        pushMsg(
          "assistant",
          "Here's what I'll enable. Reads are on; write tools stay blocked until you flip them (deny always wins).",
        );
        pushWidget("tools");
        setStage("tools");
      });
    } else {
      pace(() => {
        pushMsg("assistant", "Everything checks out. Here's exactly what gets saved:");
        pushWidget("review");
        setStage("review");
      });
    }
  }, [canContinue, pace, pushMsg, pushWidget, stage, tools.length]);

  const handleToolsContinue = useCallback(() => {
    if (stage !== "tools") return;
    pace(() => {
      pushMsg("assistant", "Everything checks out. Here's exactly what gets saved:");
      pushWidget("review");
      setStage("review");
    });
  }, [pace, pushMsg, pushWidget, stage]);

  const handleReviewCancel = useCallback(() => {
    if (stage !== "review") return;
    pushMsg("assistant", "Okay — nothing was saved. Adjust anything above and continue again, or paste something else.");
    setStage(tools.length > 0 ? "tools" : "detect");
  }, [pushMsg, stage, tools.length]);

  const runTest = useCallback(
    async (slug: string) => {
      setTesting(true);
      setTestOutcome(null);
      setFailResolved(false);
      pushWidget("test");
      try {
        const response = await testWorkspaceIntegration(accountId, slug, workspaceId);
        if (response.status === "skipped") {
          setTestOutcome({
            status: "skipped",
            note: response.reason ?? "No safe zero-argument tool to call automatically.",
          });
          setStage("done");
          pushMsg(
            "assistant",
            `${displayName} is saved. I couldn't run an automatic test (every tool needs arguments), so the first real call will validate the connection.`,
          );
          return;
        }
        const mode = response.result?.mode;
        setTestOutcome({
          status: "pass",
          summary:
            mode === "connectivity"
              ? `Connection verified with the server handshake — ${String(response.result?.tools ?? "?")} tools listed. (No zero-argument tool to invoke.)`
              : `${response.status || "ok"} · ${JSON.stringify(response.result ?? {}).slice(0, 300)}`,
        });
        setStage("done");
        pushMsg(
          "assistant",
          `🎉 ${displayName} is live for this workspace. Machines pick it up at their next session. Paste another API below whenever you're ready.`,
        );
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : "Test call failed.";
        setTestOutcome({ status: "fail", message });
      } finally {
        setTesting(false);
      }
    },
    [accountId, displayName, pushMsg, pushWidget, workspaceId],
  );

  const handleSave = useCallback(async () => {
    if (!detection || !saveRequest || saving || stage !== "review") return;
    const slug = (fieldValues.slug ?? "").trim() || detection.slug || "";
    const name = (fieldValues.display_name ?? "").trim() || detection.display_name || slug;
    const endpointValue = (fieldValues.endpoint ?? "").trim();
    const token = (fieldValues.token ?? "").trim();
    setSaving(true);
    try {
      if (detection.create_request) {
        const create = detection.create_request;
        const body: WorkspaceIntegrationCreateRequest = {
          display_name: name,
          kind: create.kind,
          transport: create.transport,
          endpoint: endpointValue || create.endpoint,
          tool_manifest: create.tool_manifest ?? [],
          ...(create.config ? { config: create.config } : {}),
          ...(token ? { token } : {}),
        };
        await createWorkspaceIntegration(accountId, slug, body, workspaceId);
      } else if (detection.import_request) {
        const body: WorkspaceIntegrationImportRequest = {
          ...detection.import_request,
          slug,
          display_name: name,
          ...(token ? { token } : {}),
        };
        if (endpointValue) {
          if (body.type === "graphql") {
            body.endpoint = endpointValue;
          } else {
            body.base_url = endpointValue;
          }
        }
        await createWorkspaceIntegrationImport(accountId, body, workspaceId);
      }
      const policy = policyFromToggles();
      // The generic create endpoint stores no tool policy, so for
      // create_request saves the default write-tool denies must be persisted
      // even when the toggles were left untouched; imports apply those
      // defaults server-side and only need explicit changes pushed.
      const mustPersistDefaults =
        Boolean(detection.create_request) && (policy.allowed.length > 0 || policy.denied.length > 0);
      if (policy.changed || mustPersistDefaults) {
        await updateWorkspaceIntegrationPolicy(
          accountId,
          slug,
          { allowed_tools: policy.allowed, denied_tools: policy.denied },
          workspaceId,
        );
      }
      setSavedSlug(slug);
      setStage("saved");
      pushMsg("assistant", `Saved ${name}. Running a read-only test call…`);
      onSaved();
      await runTest(slug);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "Saving the integration failed.";
      pushMsg("error", message);
    } finally {
      setSaving(false);
    }
  }, [accountId, detection, fieldValues, onSaved, policyFromToggles, pushMsg, runTest, saveRequest, saving, stage, workspaceId]);

  const handleRemove = useCallback(async () => {
    if (!savedSlug || removing) return;
    setRemoving(true);
    try {
      await revokeWorkspaceIntegration(accountId, savedSlug, workspaceId);
      pushMsg("assistant", "Integration removed. Paste something else whenever you're ready.");
      resetFlowState();
      onSaved();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "Failed to remove the integration.";
      pushMsg("error", message);
    } finally {
      setRemoving(false);
    }
  }, [accountId, onSaved, pushMsg, removing, resetFlowState, savedSlug, workspaceId]);

  const handleKeepAnyway = useCallback(() => {
    setFailResolved(true);
    setStage("done");
    pushMsg("assistant", "Kept the integration. You can adjust or revoke it from the Added list.");
  }, [pushMsg]);

  const handleStartOver = useCallback(() => {
    setItems([{ id: 0, type: "msg", role: "assistant", text: GREETING }]);
    itemSeq.current = 0;
    setLastInput("");
    resetFlowState();
  }, [resetFlowState]);

  const hasConversation = items.length > 1 || detection !== null;
  const busy = analyzing || pacing;

  const renderWidget = (widget: "detection" | "tools" | "review" | "test" | "connect") => {
    if (widget === "connect") {
      return connectApp ? (
        <ConnectCard
          app={connectApp}
          busy={connectBusy}
          onContinue={() => void handleConnectContinue()}
          onCancel={handleConnectCancel}
        />
      ) : null;
    }
    if (!detection && widget !== "test") return null;
    switch (widget) {
      case "detection":
        return detection ? (
          <DetectionCard
            detection={detection}
            fieldValues={fieldValues}
            onFieldChange={(key, value) => setFieldValues((current) => ({ ...current, [key]: value }))}
            active={stage === "detect"}
            saved={Boolean(savedSlug)}
            canContinue={canContinue}
            onContinue={handleDetectionContinue}
            needsReanalyze={needsReanalyze}
            analyzing={analyzing}
            onReanalyze={() => void runAnalyze(lastInput, (fieldValues.token ?? "").trim() || undefined)}
          />
        ) : null;
      case "tools":
        return detection ? (
          <ToolsCard
            displayName={displayName}
            tools={tools}
            enabledTools={enabledTools}
            onToolToggle={(name, enabled) => setEnabledTools((current) => ({ ...current, [name]: enabled }))}
            active={stage === "tools"}
            locked={Boolean(savedSlug)}
            onContinue={handleToolsContinue}
          />
        ) : null;
      case "review":
        return detection ? (
          <ReviewCard
            detection={detection}
            displayName={displayName}
            slug={(fieldValues.slug ?? "").trim() || detection.slug || ""}
            tools={tools}
            enabledTools={enabledTools}
            hasToken={Boolean((fieldValues.token ?? "").trim())}
            active={stage === "review"}
            saving={saving}
            saved={Boolean(savedSlug)}
            onConfirm={() => void handleSave()}
            onCancel={handleReviewCancel}
          />
        ) : null;
      case "test":
        return (
          <TestResultCard
            testing={testing}
            outcome={testOutcome}
            failResolved={failResolved}
            removing={removing}
            onRemove={() => void handleRemove()}
            onKeep={handleKeepAnyway}
          />
        );
    }
  };

  return (
    <section className="overflow-hidden rounded-[var(--radius-lg)] border border-border bg-card shadow-card">
      <button
        type="button"
        onClick={toggleOpen}
        aria-expanded={open}
        className="flex w-full items-center gap-3 border-b border-border-subtle bg-surface px-4 py-3 text-left"
      >
        <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-[9px] bg-gradient-to-br from-brand-600 to-brand-400 text-white">
          <Sparkles className="h-4 w-4" />
        </span>
        <span className="flex-1">
          <span className="block text-sm font-semibold text-text-primary">Add an integration</span>
          <span className="block text-xs text-text-tertiary">Paste anything &mdash; I&apos;ll figure out the rest</span>
        </span>
        {open ? (
          <ChevronDown className="h-4 w-4 text-text-tertiary" />
        ) : (
          <ChevronRight className="h-4 w-4 text-text-tertiary" />
        )}
      </button>

      {open && (
        <div className="flex flex-col">
          <div
            ref={scrollRef}
            role="log"
            aria-live="polite"
            aria-label="Integration copilot conversation"
            className="max-h-[520px] space-y-4 overflow-y-auto px-4 py-4"
          >
            {items.map((item) =>
              item.type === "msg" ? (
                <ChatRow key={item.id} role={item.role} text={item.text} />
              ) : (
                <WidgetRow key={item.id}>{renderWidget(item.widget)}</WidgetRow>
              ),
            )}
            {busy && <TypingRow status={analyzing ? liveStatus : null} />}
          </div>

          <div className="border-t border-border-subtle bg-surface px-4 py-3">
            {quickAdds.length > 0 && (
              <div className="mb-2.5 flex flex-wrap items-center gap-1.5">
                {quickAdds.map((app) => (
                  <button
                    key={app.id}
                    type="button"
                    onClick={() => handleQuickAdd(app)}
                    className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-3 py-1 text-xs text-text-secondary transition-colors hover:border-brand-500 hover:text-text-primary"
                  >
                    {app.Icon && <app.Icon className="h-3.5 w-3.5" />}
                    {app.name}
                    {app.connected && <CheckCircle2 className="h-3 w-3 text-green-400" />}
                  </button>
                ))}
                <a
                  href="#integration-catalog"
                  className="ml-1 text-[11px] text-text-tertiary underline hover:text-text-secondary"
                >
                  Browse all
                </a>
              </div>
            )}
            <div className="flex items-end gap-2">
              <textarea
                value={input}
                onChange={(event) => setInput(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" && !event.shiftKey) {
                    event.preventDefault();
                    handleSend();
                  }
                }}
                rows={2}
                aria-label="Paste an integration source"
                placeholder={PASTE_PLACEHOLDER}
                className="w-full flex-1 resize-none rounded-[var(--radius)] border border-border bg-input px-3 py-2.5 font-mono text-xs text-text-primary outline-none placeholder:font-body focus:border-border-hover"
              />
              <button
                type="button"
                onClick={handleSend}
                aria-label="Analyze"
                title="Analyze"
                disabled={analyzing || !input.trim()}
                className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-[var(--radius)] bg-brand-500 text-white transition-colors hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-40"
              >
                {analyzing ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowUp className="h-4 w-4" />}
              </button>
            </div>
            <div className="mt-2 flex items-center justify-between gap-3 text-xs text-text-tertiary">
              <span>
                Reads on, writes blocked by default. Nothing saves until you confirm.{" "}
                <a href="#integration-catalog" className="underline hover:text-text-secondary">
                  Use classic form
                </a>
              </span>
              {hasConversation && (
                <button
                  type="button"
                  onClick={handleStartOver}
                  className="flex-shrink-0 rounded-full border border-border px-3 py-1 text-[11px] text-text-tertiary hover:text-text-primary"
                >
                  ↺ Start over
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

// ---------- transcript rows ----------

function AssistantAvatar() {
  return (
    <span className="mt-0.5 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-[8px] bg-gradient-to-br from-brand-600 to-brand-400 text-white">
      <Sparkles className="h-3.5 w-3.5" />
    </span>
  );
}

function ChatRow({ role, text }: { role: "user" | "assistant" | "clarify" | "error"; text: string }) {
  if (role === "user") {
    return (
      <div className="flex items-start gap-3">
        <span className="mt-0.5 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-[8px] border border-border bg-elevated text-[10px] font-semibold text-text-secondary">
          You
        </span>
        <div className="min-w-0 flex-1 rounded-[var(--radius)] border border-border-subtle bg-input px-3 py-2">
          <pre className="max-h-24 overflow-hidden whitespace-pre-wrap break-all font-mono text-[11px] leading-relaxed text-text-secondary">
            {text}
          </pre>
        </div>
      </div>
    );
  }
  if (role === "error") {
    return (
      <div className="flex items-start gap-3">
        <AssistantAvatar />
        <div role="alert" className="flex items-start gap-2 rounded-[var(--radius)] border border-red-600/30 bg-red-600/10 px-3 py-2 text-sm text-red-400">
          <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
          <span>{text}</span>
        </div>
      </div>
    );
  }
  return (
    <div className="flex items-start gap-3">
      <AssistantAvatar />
      <p className={`min-w-0 flex-1 pt-1 text-sm ${role === "clarify" ? "text-text-secondary" : "text-text-primary"}`}>
        {text}
      </p>
    </div>
  );
}

// Widgets hang off the preceding assistant message, so they indent past the
// avatar column instead of repeating it.
function WidgetRow({ children }: { children: React.ReactNode }) {
  if (!children) return null;
  return (
    <div className="flex items-start gap-3">
      <span aria-hidden className="w-7 flex-shrink-0" />
      <div className="min-w-0 flex-1">{children}</div>
    </div>
  );
}

function TypingRow({ status }: { status?: string | null }) {
  return (
    <div className="flex items-start gap-3" aria-label="Copilot is thinking">
      <AssistantAvatar />
      <span className="flex items-center gap-2 pt-2">
        <span className="flex items-center gap-1">
          {[0, 1, 2].map((index) => (
            <span
              key={index}
              className="h-1.5 w-1.5 rounded-full bg-text-tertiary motion-safe:animate-pulse"
              style={{ animationDelay: `${index * 200}ms` }}
            />
          ))}
        </span>
        {status && <span className="text-xs text-text-tertiary">{status}</span>}
      </span>
    </div>
  );
}

// ---------- widget cards ----------

function WidgetFrame({
  icon,
  title,
  badge,
  children,
  footer,
}: {
  icon: React.ReactNode;
  title: string;
  badge?: React.ReactNode;
  children: React.ReactNode;
  footer?: React.ReactNode;
}) {
  return (
    <div className="overflow-hidden rounded-[var(--radius-lg)] border border-border bg-card shadow-card">
      <div className="flex items-center gap-2.5 border-b border-border-subtle px-4 py-3">
        <span className="text-sm">{icon}</span>
        <span className="text-[13px] font-semibold text-text-primary">{title}</span>
        {badge && <span className="ml-auto">{badge}</span>}
      </div>
      <div className="space-y-3 px-4 py-3 text-[13px]">{children}</div>
      {footer && (
        <div className="flex flex-wrap items-center gap-2.5 border-t border-border-subtle bg-white/[0.015] px-4 py-3">
          {footer}
        </div>
      )}
    </div>
  );
}

function PrimaryButton({
  onClick,
  disabled,
  busy,
  id,
  children,
}: {
  onClick: () => void;
  disabled?: boolean;
  busy?: boolean;
  id?: string;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      id={id}
      onClick={onClick}
      disabled={disabled}
      className="inline-flex items-center gap-2 whitespace-nowrap rounded-[var(--radius-sm)] bg-brand-500 px-4 py-1.5 text-[13px] font-semibold text-white transition-colors hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-45"
    >
      {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
      {children}
    </button>
  );
}

function GhostButton({ onClick, disabled, children }: { onClick: () => void; disabled?: boolean; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="whitespace-nowrap rounded-[var(--radius-sm)] border border-border px-4 py-1.5 text-[13px] font-semibold text-text-secondary transition-colors hover:bg-card-hover hover:text-text-primary disabled:cursor-not-allowed disabled:opacity-45"
    >
      {children}
    </button>
  );
}

function FootNote({ children }: { children: React.ReactNode }) {
  return <span className="ml-auto text-[11px] text-text-tertiary">{children}</span>;
}

// One editable config value on a detection card. Required-and-empty chips get
// the amber attention treatment; secret chips render as password inputs and
// use the stable token id so the chat's secret guard can focus them.
function FieldChip({
  field,
  value,
  disabled,
  secret = false,
  onChange,
}: {
  field: { key: string; label: string; required: boolean };
  value: string;
  disabled: boolean;
  secret?: boolean;
  onChange: (value: string) => void;
}) {
  const attention = field.required && !value.trim();
  const inputId = secret && field.key === "token" ? TOKEN_INPUT_ID : `copilot-field-${field.key}`;
  return (
    <div
      className={`rounded-[var(--radius-sm)] border bg-input px-3 py-2 ${
        attention ? "border-yellow-400 shadow-[0_0_0_1px_rgba(250,204,21,0.25)]" : "border-border-subtle"
      }`}
    >
      <label
        htmlFor={inputId}
        className={`block text-[10px] font-semibold uppercase tracking-wider ${
          attention ? "text-yellow-400" : "text-text-tertiary"
        }`}
      >
        {field.label}
        {attention ? " — required" : ""}
      </label>
      <input
        id={inputId}
        type={secret ? "password" : "text"}
        {...(secret ? { autoComplete: "new-password" } : {})}
        value={value}
        disabled={disabled}
        onChange={(event) => onChange(event.target.value)}
        className="w-full bg-transparent font-mono text-xs text-text-primary outline-none placeholder:text-text-muted"
      />
    </div>
  );
}

function Diagnostics({ detection }: { detection: WorkspaceIntegrationCopilotDetection }) {
  return (
    <>
      {(detection.diagnostics ?? []).map((diagnostic, index) => (
        <div key={index} className="flex items-baseline gap-2 text-xs text-text-secondary">
          <span
            className={
              diagnostic.level === "ok" ? "text-green-400" : diagnostic.level === "warn" ? "text-yellow-400" : "text-red-400"
            }
          >
            {diagnostic.level === "ok" ? "●" : "▲"}
          </span>
          <span>{diagnostic.text}</span>
        </div>
      ))}
    </>
  );
}

function DetectionCard({
  detection,
  fieldValues,
  onFieldChange,
  active,
  saved,
  canContinue,
  onContinue,
  needsReanalyze,
  analyzing,
  onReanalyze,
}: {
  detection: WorkspaceIntegrationCopilotDetection;
  fieldValues: Record<string, string>;
  onFieldChange: (key: string, value: string) => void;
  active: boolean;
  saved: boolean;
  canContinue: boolean;
  onContinue: () => void;
  needsReanalyze: boolean;
  analyzing: boolean;
  onReanalyze: () => void;
}) {
  const [showRaw, setShowRaw] = useState(false);
  const fields = detection.fields ?? [];
  // A detection with no save body and no token to re-analyze with (e.g. an
  // OAuth-protected MCP server) is informational — its next step lives
  // outside the chat, so no primary action is offered.
  const savable = Boolean(detection.create_request || detection.import_request);
  // Secret fields never render in the generic chips grid — they get a
  // dedicated Authorization section at the bottom of the card.
  const plainFields = fields.filter((field) => !field.secret);
  const secretFields = fields.filter((field) => field.secret);
  const tokenValue = (fieldValues.token ?? "").trim();
  const credentialGenerateUrl = safeExternalHttpUrl(detection.credential_hint?.generate_url);

  const rawConfig = useMemo(() => {
    const base = detection.create_request ?? detection.import_request;
    if (!base) return null;
    const merged: Record<string, unknown> = { ...base };
    const slug = (fieldValues.slug ?? "").trim();
    const displayName = (fieldValues.display_name ?? "").trim();
    const endpoint = (fieldValues.endpoint ?? "").trim();
    if (slug) merged.slug = slug;
    if (displayName) merged.display_name = displayName;
    if (endpoint) {
      if (detection.create_request || detection.import_request?.type === "graphql") {
        merged.endpoint = endpoint;
      } else {
        merged.base_url = endpoint;
      }
    }
    // Never render the secret, even if a caller ever set one on the request.
    delete merged.token;
    return JSON.stringify(merged, null, 2);
  }, [detection, fieldValues]);

  return (
    <WidgetFrame
      icon={detection.kind === "mcp-remote" ? "🔌" : detection.kind === "graphql" ? "◈" : detection.kind === "llms-txt" ? "🗺️" : "📄"}
      title={detection.display_name || "Detected integration"}
      badge={
        saved ? (
          <span className="inline-flex items-center gap-1 rounded-full bg-green-400/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-green-400">
            <CheckCircle2 className="h-3 w-3" />
            Saved
          </span>
        ) : (
          <span className="whitespace-nowrap rounded-full bg-teal-400/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-teal-400">
            {KIND_LABELS[detection.kind] ?? detection.kind}
          </span>
        )
      }
      footer={
        !saved &&
        (needsReanalyze || savable) && (
          <>
            {needsReanalyze ? (
              <PrimaryButton onClick={onReanalyze} disabled={analyzing || !tokenValue} busy={analyzing}>
                Re-analyze with token
              </PrimaryButton>
            ) : (
              <PrimaryButton onClick={onContinue} disabled={!active || !canContinue}>
                Looks right &mdash; continue
              </PrimaryButton>
            )}
            <FootNote>Nothing is saved until the last step</FootNote>
          </>
        )
      }
    >
      <Diagnostics detection={detection} />

      {plainFields.length > 0 && (
        <div className="grid gap-2 sm:grid-cols-2">
          {plainFields.map((field) => (
            <FieldChip
              key={field.key}
              field={field}
              value={fieldValues[field.key] ?? ""}
              disabled={saved}
              onChange={(value) => onFieldChange(field.key, value)}
            />
          ))}
        </div>
      )}

      {rawConfig && (
        <div>
          <button
            type="button"
            onClick={() => setShowRaw((current) => !current)}
            className="text-xs text-text-tertiary underline hover:text-text-secondary"
          >
            {showRaw ? "Hide raw config" : "View raw config (JSON)"}
          </button>
          {showRaw && (
            <pre className="mt-2 overflow-x-auto rounded-[var(--radius-sm)] border border-border-subtle bg-input px-3 py-2 font-mono text-[11px] text-text-secondary">
              {rawConfig}
            </pre>
          )}
        </div>
      )}

      {secretFields.length > 0 && (
        <div className="border-t border-border-subtle pt-3">
          <div className="mb-2 flex items-center gap-1.5">
            <Lock className="h-3.5 w-3.5 text-text-tertiary" />
            <span className="text-[10px] font-semibold uppercase tracking-wider text-text-tertiary">
              {detection.credential_hint?.label ?? "Authorization"}
            </span>
            {credentialGenerateUrl && (
              <a
                href={credentialGenerateUrl}
                target="_blank"
                rel="noreferrer"
                className="ml-auto text-[11px] font-medium text-brand-500 hover:text-brand-600"
              >
                Get token ↗
              </a>
            )}
          </div>
          {detection.credential_hint?.setup && (
            <p className="mb-2 text-[11px] text-text-secondary">{detection.credential_hint.setup}</p>
          )}
          {secretFields.map((field) => (
            <div key={field.key}>
              <FieldChip
                field={field}
                value={fieldValues[field.key] ?? ""}
                disabled={saved}
                secret
                onChange={(value) => onFieldChange(field.key, value)}
              />
              <p className="mt-1.5 text-[11px] text-text-tertiary">
                Stored encrypted server-side. Machines never see it. Don&apos;t paste tokens into the chat.
              </p>
            </div>
          ))}
        </div>
      )}
    </WidgetFrame>
  );
}

function ToolsCard({
  displayName,
  tools,
  enabledTools,
  onToolToggle,
  active,
  locked,
  onContinue,
}: {
  displayName: string;
  tools: WorkspaceIntegrationCatalogTool[];
  enabledTools: Record<string, boolean>;
  onToolToggle: (name: string, enabled: boolean) => void;
  active: boolean;
  locked: boolean;
  onContinue: () => void;
}) {
  const enabledCount = tools.filter((tool) => enabledTools[tool.name]).length;
  return (
    <WidgetFrame
      icon="🧰"
      title={`Tools · ${displayName}`}
      footer={
        !locked && (
          <>
            <PrimaryButton onClick={onContinue} disabled={!active} id={active ? "copilot-active-primary" : undefined}>
              Continue
            </PrimaryButton>
            <FootNote>Change any of this later on the detail page</FootNote>
          </>
        )
      }
    >
      <div className="max-h-56 space-y-0.5 overflow-y-auto">
        {tools.map((tool) => (
          <label
            key={tool.name}
            className={`flex items-center gap-2.5 rounded-[var(--radius-sm)] px-2 py-1.5 ${locked ? "" : "cursor-pointer hover:bg-card-hover"}`}
          >
            <input
              type="checkbox"
              aria-label={tool.name}
              checked={Boolean(enabledTools[tool.name])}
              disabled={locked}
              onChange={(event) => onToolToggle(tool.name, event.target.checked)}
              className="peer sr-only"
            />
            <span
              aria-hidden
              className="relative h-[17px] w-[30px] flex-shrink-0 rounded-full border border-border bg-elevated transition-colors peer-checked:border-brand-600 peer-checked:bg-brand-600 after:absolute after:left-[2px] after:top-[1.5px] after:h-3 after:w-3 after:rounded-full after:bg-text-tertiary after:transition-transform peer-checked:after:translate-x-3 peer-checked:after:bg-white"
            />
            <span className="font-mono text-xs text-text-primary">{tool.name}</span>
            <span
              className={`rounded px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider ${
                tool.access === "Write" ? "bg-red-400/10 text-red-400" : "bg-blue-400/10 text-blue-400"
              }`}
            >
              {tool.access}
            </span>
            {tool.description && (
              <span className="min-w-0 flex-1 truncate text-[11px] text-text-tertiary">{tool.description}</span>
            )}
          </label>
        ))}
      </div>
      <p className="text-xs text-text-secondary">
        <span className="font-semibold text-text-primary">
          {enabledCount} of {tools.length}
        </span>{" "}
        tools will be enabled &middot; write tools stay blocked until you allow them (deny always wins).
      </p>
    </WidgetFrame>
  );
}

function ReviewCard({
  detection,
  displayName,
  slug,
  tools,
  enabledTools,
  hasToken,
  active,
  saving,
  saved,
  onConfirm,
  onCancel,
}: {
  detection: WorkspaceIntegrationCopilotDetection;
  displayName: string;
  slug: string;
  tools: WorkspaceIntegrationCatalogTool[];
  enabledTools: Record<string, boolean>;
  hasToken: boolean;
  active: boolean;
  saving: boolean;
  saved: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const enabled = tools.filter((tool) => enabledTools[tool.name]);
  const reads = enabled.filter((tool) => tool.access !== "Write").length;
  const writes = enabled.length - reads;
  const transport = detection.create_request?.transport ?? detection.import_request?.type ?? detection.kind;
  const rows: Array<[string, string]> = [
    ["Integration", `${slug} · ${transport}`],
    ["Tools enabled", `${reads} read · ${writes} write`],
    ["Policy", writes === 0 ? "writes blocked (deny wins)" : "unchecked tools blocked (deny wins)"],
    ["Credentials", hasToken ? "stored encrypted server-side — never on machines" : "none required"],
  ];
  return (
    <WidgetFrame
      icon="💾"
      title={`Add ${displayName} to this workspace`}
      footer={
        !saved && (
          <>
            <PrimaryButton
              onClick={onConfirm}
              disabled={!active || saving}
              busy={saving}
              id={active ? "copilot-active-primary" : undefined}
            >
              Add integration
            </PrimaryButton>
            <GhostButton onClick={onCancel} disabled={!active || saving}>
              Cancel
            </GhostButton>
            <FootNote>You can revoke anytime</FootNote>
          </>
        )
      }
    >
      <div>
        {rows.map(([label, value]) => (
          <div key={label} className="flex items-baseline justify-between gap-4 border-b border-border-subtle py-1.5 text-xs last:border-b-0">
            <span className="text-text-tertiary">{label}</span>
            <span className="text-right font-mono text-[11px] text-text-primary">{value}</span>
          </div>
        ))}
      </div>
    </WidgetFrame>
  );
}

function ConnectCard({
  app,
  busy,
  onContinue,
  onCancel,
}: {
  app: CopilotQuickAdd;
  busy: boolean;
  onContinue: () => void;
  onCancel: () => void;
}) {
  return (
    <WidgetFrame
      icon={app.Icon ? <app.Icon className="h-4 w-4 text-text-primary" /> : "🔗"}
      title={`Connect ${app.name}`}
      badge={
        <span className="rounded-full bg-teal-400/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-teal-400">
          Pre-built
        </span>
      }
      footer={
        <>
          <PrimaryButton onClick={onContinue} disabled={busy} busy={busy}>
            Continue to {app.developer ?? "provider"}
          </PrimaryButton>
          <GhostButton onClick={onCancel} disabled={busy}>
            Cancel
          </GhostButton>
          <FootNote>You approve on the provider&apos;s screen</FootNote>
        </>
      }
    >
      <div className="flex items-baseline gap-2 text-xs text-text-secondary">
        <span className="text-green-400">●</span>
        <span>{app.permissionsSummary ?? COPILOT_DEFAULT_PERMISSIONS_SUMMARY}</span>
      </div>
      <div className="flex items-baseline gap-2 text-xs text-text-secondary">
        <span className="text-green-400">●</span>
        <span>Credentials are stored encrypted server-side — machines never see them.</span>
      </div>
      <p className="text-[11px] text-text-tertiary">
        You can fine-tune per-service permissions anytime on the {app.name} detail page.
      </p>
    </WidgetFrame>
  );
}

function TestResultCard({
  testing,
  outcome,
  failResolved,
  removing,
  onRemove,
  onKeep,
}: {
  testing: boolean;
  outcome: TestOutcome | null;
  failResolved: boolean;
  removing: boolean;
  onRemove: () => void;
  onKeep: () => void;
}) {
  return (
    <WidgetFrame
      icon="🧪"
      title="Test call"
      badge={
        testing ? (
          <span className="inline-flex items-center gap-1 text-xs text-text-tertiary">
            <Loader2 className="h-3 w-3 animate-spin" />
            Running&hellip;
          </span>
        ) : outcome?.status === "pass" ? (
          <span className="rounded-full bg-green-400/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-green-400">
            Passed
          </span>
        ) : outcome?.status === "skipped" ? (
          <span className="rounded-full bg-yellow-400/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-yellow-400">
            Not auto-tested
          </span>
        ) : outcome ? (
          <span className="rounded-full bg-red-400/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-red-400">
            Failed
          </span>
        ) : undefined
      }
      footer={
        outcome?.status === "fail" && !failResolved ? (
          <>
            <button
              type="button"
              onClick={onRemove}
              disabled={removing}
              className="inline-flex items-center gap-2 rounded-[var(--radius-sm)] border border-red-600/40 px-4 py-1.5 text-[13px] font-semibold text-red-400 transition-colors hover:bg-red-600/10 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {removing && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              Remove integration
            </button>
            <GhostButton onClick={onKeep}>Keep anyway</GhostButton>
          </>
        ) : undefined
      }
    >
      {testing ? (
        <p className="text-sm text-text-secondary">Calling the safest read-only tool&hellip;</p>
      ) : outcome?.status === "pass" ? (
        <div className="flex items-start gap-2 text-sm text-text-secondary">
          <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-green-400" />
          <span className="break-all font-mono text-xs">{outcome.summary}</span>
        </div>
      ) : outcome?.status === "skipped" ? (
        <p className="text-sm text-text-secondary">{outcome.note}</p>
      ) : outcome ? (
        <div className="flex items-start gap-2 text-sm text-red-400">
          <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
          <span>{outcome.message}</span>
        </div>
      ) : null}
    </WidgetFrame>
  );
}
